// DataFormDialog.tsx — Anlegen/Bearbeiten/Löschen eines Eintrags der Sektionen
// Anschriften / Kontaktdaten / Bankverbindungen im "Daten ändern"-Modal der
// ContactDetails. POST (neu) / PUT (bearbeiten) / DELETE an die bestehenden
// contact/:id/{address|communication|bank}[/:id]-Endpoints.
// Hinweis (Entscheidung 75/F6): Der Kommunikationswege-Tab heißt jetzt
// „Kontaktdaten" (Backend-Endpoint bleibt `communication`).

import { useState } from "react";
import { plzPruefen } from "../forms/validation/laenderprofil";
import { LAENDER_AUSWAHL } from "../../data/laender";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { apiUrl } from "../../utils/api";
import { AddressAutocomplete } from "../forms/widgets/AddressAutocomplete";
import { IbanEingabe } from "../forms/widgets/IbanEingabe";
import { formatIbanForDisplay, normalizeBic, validateIban } from "../forms/validation/iban";
import type { BankTreffer } from "../forms/widgets/bankleitzahlen";
import { TelefonEingabe } from "../forms/widgets/TelefonEingabe";
import { useFirmenZentralen } from "../forms/widgets/firmenZentrale";
import { ortsteilLeeren } from "../../utils/ortsteilAbgleich";
import { bestaetigen } from "./bestaetigen";
import { UnternehmenMehrfachWahl } from "../forms/widgets/UnternehmenMehrfachWahl";

export type DataTab = "Anschriften" | "Kontaktdaten" | "Bankverbindungen";

const endpointFor = (tab: DataTab): string =>
  tab === "Anschriften" ? "address" : tab === "Kontaktdaten" ? "communication" : "bank";

const btn = (bg: string, fg = "#fff"): React.CSSProperties => ({
  padding: "10px 20px",
  borderRadius: 11,
  border: "none",
  cursor: "pointer",
  font: '700 14px/1 "Hanken Grotesk", sans-serif',
  background: bg,
  color: fg,
});

function Field({ label, value, onChange, placeholder }: { label: string; value?: unknown; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label style={{ display: "grid", rowGap: 5 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "#6b5a66" }}>{label}</span>
      <input
        value={value == null ? "" : String(value)}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ padding: "8px 10px", border: "1px solid #ddd3dc", borderRadius: 8, font: "inherit" }}
      />
    </label>
  );
}

/** Telefon-Feld: dasselbe Aussehen wie `Field`, im Format „+49 221 123456-78". */
function TelefonField({ label, value, onChange, placeholder, geschaeftlich, zentrale }: { label: string; value?: unknown; onChange: (v: string) => void; placeholder?: string; geschaeftlich?: boolean; zentrale?: readonly string[] }) {
  return (
    <label style={{ display: "grid", rowGap: 5 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "#6b5a66" }}>{label}</span>
      <TelefonEingabe
        value={value == null ? "" : String(value)}
        placeholder={placeholder}
        onChange={onChange}
        geschaeftlich={geschaeftlich}
        zentrale={zentrale}
        style={{ padding: "8px 10px", border: "1px solid #ddd3dc", borderRadius: 8, font: "inherit" }}
      />
    </label>
  );
}

/** IBAN-Feld: dasselbe Aussehen wie `Field`, aber mit 4er-Gruppen beim Tippen. */
function IbanField({ label, value, onChange, onBankErkannt }: { label: string; value?: unknown; onChange: (v: string) => void; onBankErkannt?: (b: BankTreffer) => void }) {
  const text = value == null ? "" : String(value);
  const pruefung = validateIban(text);
  const fehler = text && !pruefung.valid ? pruefung.error : undefined;
  return (
    <label style={{ display: "grid", rowGap: 5 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "#6b5a66" }}>{label}</span>
      <IbanEingabe
        value={text}
        onChange={onChange}
        onBankErkannt={onBankErkannt}
        placeholder="DE00 0000 0000 0000 0000 00"
        aria-invalid={!!fehler}
        style={{ padding: "8px 10px", border: `1px solid ${fehler ? "#c23b3b" : "#ddd3dc"}`, borderRadius: 8, font: "inherit" }}
      />
      {fehler && <span style={{ color: "#c23b3b", fontSize: 12 }}>{fehler}</span>}
    </label>
  );
}

/**
 * Land als Auswahl über den Länderkatalog — im Aussehen des Dialogs, damit es
 * nicht wie ein Fremdkörper zwischen den Inline-Feldern steht.
 */
function LandFeld({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Sel
      label="Land"
      value={value}
      onChange={onChange}
      options={LAENDER_AUSWAHL.map((l) => ({ value: l, label: l }))}
    />
  );
}

/** PLZ mit dem Hinweis, der zum gewählten Land gehört. */
function PlzFeld({ value, land, onChange }: { value: string; land: string; onChange: (v: string) => void }) {
  const urteil = plzPruefen(value, land);
  return (
    <label style={{ display: "grid", rowGap: 5 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "#6b5a66" }}>PLZ</span>
      <input
        value={value}
        placeholder={urteil.beispiel}
        inputMode={urteil.nurZiffern ? "numeric" : "text"}
        onChange={(e) => onChange(e.target.value)}
        style={{ padding: "8px 10px", border: `1px solid ${urteil.ok ? "#ddd3dc" : "#c23b3b"}`, borderRadius: 8, font: "inherit" }}
      />
      {/* Ein Hinweis, keine Sperre: Der Dialog hat die PLZ nie geprüft, und
          Bestandsadressen sollen sich weiter speichern lassen. */}
      {urteil.meldung && <span style={{ color: "#c23b3b", fontSize: 12 }}>{urteil.meldung}</span>}
    </label>
  );
}

function Sel({ label, value, onChange, options }: { label: string; value?: unknown; onChange: (v: string) => void; options: Array<{ value: string; label: string }> }) {
  return (
    <label style={{ display: "grid", rowGap: 5 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "#6b5a66" }}>{label}</span>
      <select
        value={value == null ? "" : String(value)}
        onChange={(e) => onChange(e.target.value)}
        style={{ padding: "8px 10px", border: "1px solid #ddd3dc", borderRadius: 8, font: "inherit", background: "#fff" }}
      >
        <option value="">— wählen —</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span style={{ fontSize: 13, color: "#241522" }}>{label}</span>
    </label>
  );
}

/** Kern-Kommunikationstypen, an denen die Art (privat/geschäftlich) erfasst wird. */
const KERN_TYPEN = ["phone", "mobile", "email", "fax"];

/**
 * Art-Vorbelegung einer Bestandszeile: strukturiertes `kind` zuerst, sonst
 * Label-Heuristik (Substring, wie der SP1-Backfill); nichts erkennbar → leer
 * und beim Speichern Pflicht (schrittweise Nachpflege beim Anfassen).
 */
const guessKind = (row: Record<string, any> | null | undefined): string => {
  const k = row?.kind;
  if (k === "privat" || k === "geschaeftlich") return k;
  const l = String(row?.label ?? "").toLowerCase();
  const hatGesch = l.includes("geschäftlich") || l.includes("geschaeftlich");
  if (l.includes("privat") && !hatGesch) return "privat";
  if (hatGesch && !l.includes("privat")) return "geschaeftlich";
  return "";
};

type FirmaWahl = { id: number; name: string };

/** Gespeicherte Unternehmen einer Bestandszeile (API: `companies`, Altstand: `companyId`). */
function bestandsFirmen(existing: Record<string, any> | null | undefined): FirmaWahl[] {
  if (!existing) return [];
  if (Array.isArray(existing.companies)) {
    return (existing.companies as any[])
      .map((f) => ({ id: Number(f?.id), name: String(f?.name ?? "").trim() }))
      .filter((f) => Number.isInteger(f.id) && f.id > 0);
  }
  const id = Number(existing.companyId);
  return Number.isInteger(id) && id > 0 ? [{ id, name: String(existing.company?.displayName ?? `Firma ${id}`) }] : [];
}

/** Genau ein wählbares Unternehmen ist immer dabei (vorn, wenn es fehlte). */
function firmenMitEinzigem(gewaehlt: FirmaWahl[] | undefined, firmen: FirmaWahl[] | undefined): FirmaWahl[] {
  const basis = gewaehlt ?? [];
  if (firmen?.length === 1 && !basis.some((f) => f.id === firmen[0].id)) return [firmen[0], ...basis];
  return basis;
}

export function DataFormDialog({
  tab,
  contactId,
  existing,
  onClose,
  onSaved,
  istFirma,
  firmen,
  kontoinhaber,
}: {
  tab: DataTab;
  contactId: number;
  existing?: Record<string, any> | null;
  onClose: () => void;
  onSaved: () => void;
  /** true = Firmen-Kontakt: kein Art-Dropdown, `kind` bleibt NULL. */
  istFirma?: boolean;
  /**
   * Wählbare Unternehmen der Person („geschäftlich bei …") — nur aktive
   * Verbindungen, ausgeschiedene nicht. Genau eins → immer gesetzt.
   */
  firmen?: Array<{ id: number; name: string }>;
  /**
   * Name des Kontakts in Kontoinhaber-Schreibweise. Belegt eine NEUE
   * Bankverbindung vor — in der Regel gehört das Konto der Person bzw. der
   * Firma, deren Akte gerade offen ist. Beim Bearbeiten einer bestehenden
   * Zeile bleibt ein vorhandener Inhaber unangetastet.
   */
  kontoinhaber?: string;
}) {
  const isEdit = !!(existing && existing.id);
  const [form, setForm] = useState<Record<string, any>>(() => {
    const kind = istFirma ? "" : guessKind(existing);
    return {
      ...(existing ?? {}),
      kind,
      // Mehrere Unternehmen je Weg (02.10.2026). Genau eins → immer gesetzt,
      // auch beim Nachpflegen einer Bestandszeile.
      firmenWahl: firmenMitEinzigem(
        kind === "geschaeftlich" ? bestandsFirmen(existing) : [],
        kind === "geschaeftlich" ? firmen : undefined,
      ),
      // IBAN aus dem Bestand kann ungruppiert sein (Import, Zusammenführung) —
      // im Feld steht sie ab jetzt immer in 4er-Blöcken.
      ...(tab === "Bankverbindungen"
        ? {
            iban: formatIbanForDisplay(String(existing?.iban ?? "")),
            accountHolder: String(existing?.accountHolder ?? "").trim() || kontoinhaber || "",
          }
        : {}),
    };
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const up = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));
  /**
   * PLZ oder Ort ändern — ein Ortsteil, der nicht mehr passt, fällt dabei weg
   * (utils/ortsteilAbgleich.ts, User 30.09.2026).
   */
  const upPlzOderOrt = (k: "postalCode" | "city", v: string) =>
    setForm((f) => {
      const vorher = { plz: String(f.postalCode ?? ""), ort: String(f.city ?? ""), ortsteil: String(f.ortsteil ?? "") };
      const leeren = ortsteilLeeren(vorher, k === "postalCode" ? { plz: v } : { ort: v });
      return { ...f, [k]: v, ...(leeren ? { ortsteil: "" } : {}) };
    });
  // Geschäftliche Festnetz-/Faxnummer einer Person: die Nummern der Firma, zu
  // der sie gehört (gewählt, sonst alle verbundenen), sind die Zentrale für
  // die automatische Durchwahl beim Verlassen des Feldes.
  const firmenZentrale = useFirmenZentralen(
    !istFirma && form.kind === "geschaeftlich" && (form.type === "phone" || form.type === "fax")
      ? (form.firmenWahl as FirmaWahl[]).length
        ? (form.firmenWahl as FirmaWahl[]).map((f) => f.id)
        : (firmen ?? []).map((f) => f.id)
      : [],
  );

  /** BIC und Bankname aus der IBAN — nur in leere Felder, nie überschreibend. */
  const bankUebernehmen = (bank: BankTreffer) => {
    setForm((f) => {
      const leer = (v: unknown) => !String(v ?? "").trim();
      const nd = { ...f };
      if (bank.bic && leer(f.bic)) nd.bic = bank.bic;
      if (leer(f.bankName)) nd.bankName = bank.bankName;
      return nd;
    });
  };

  const istKernTyp = KERN_TYPEN.includes(String(form.type ?? ""));

  const validate = (): boolean => {
    if (tab === "Anschriften" && (!String(form.street ?? "").trim() || !String(form.city ?? "").trim())) {
      setErr("Straße und Ort sind erforderlich.");
      return false;
    }
    if (tab === "Kontaktdaten") {
      if (!String(form.type ?? "").trim() || !String(form.value ?? "").trim()) {
        setErr("Typ und Nummer/Adresse sind erforderlich.");
        return false;
      }
      if (!istFirma && istKernTyp && form.kind !== "privat" && form.kind !== "geschaeftlich") {
        setErr("Bitte die Art wählen (privat oder geschäftlich).");
        return false;
      }
    }
    return true;
  };

  const buildPayload = (): Record<string, unknown> => {
    if (tab === "Anschriften") {
      return {
        addressDescription: form.addressDescription || null,
        street: form.street || null,
        city: form.city || null,
        postalCode: form.postalCode || null,
        ortsteil: form.ortsteil || null,
        country: form.country || null,
        isDefault: !!form.isDefault,
      };
    }
    if (tab === "Kontaktdaten") {
      // Art strukturiert in `kind` (ASCII), Bezeichnung bleibt Freitext-Label.
      // Firmen-Zeilen und Social-Typen tragen keine Art; die Firmen-Zuordnung
      // gibt es nur zusammen mit kind='geschaeftlich' (Server validiert).
      const kind =
        !istFirma && istKernTyp && (form.kind === "privat" || form.kind === "geschaeftlich")
          ? form.kind
          : null;
      return {
        type: form.type,
        label: form.label || null,
        value: form.value,
        isDefault: !!form.isDefault,
        kind,
        // Alle Unternehmen; leer = keine Zuordnung (ab zwei erlaubt).
        companyIds: kind === "geschaeftlich" ? (form.firmenWahl as FirmaWahl[]).map((f) => Number(f.id)) : [],
      };
    }
    return {
      bankName: form.bankName || null,
      // Gespeichert wird die gruppierte Form — so steht sie seit jeher in der
      // Datenbank (Seeds, Bestand), und so ist sie im Beleg lesbar.
      iban: formatIbanForDisplay(String(form.iban ?? "")) || null,
      bic: normalizeBic(String(form.bic ?? "")) || null,
      accountHolder: form.accountHolder || null,
      bankDescription: form.bankDescription || null,
      isDefault: !!form.isDefault,
      directDebit: !!form.directDebit,
    };
  };

  const submit = async () => {
    setErr("");
    if (!validate()) return;
    setSaving(true);
    try {
      const ep = endpointFor(tab);
      const url = isEdit
        ? apiUrl(`contact/${contactId}/${ep}/${existing!.id}`)
        : apiUrl(`contact/${contactId}/${ep}`);
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error(await res.text());
      onSaved();
    } catch {
      setErr("Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!isEdit) return;
    if (
      !(await bestaetigen({
        titel: "Eintrag löschen?",
        text: "Der Eintrag wird entfernt.",
        ja: "Löschen",
        ton: "danger",
      }))
    )
      return;
    setSaving(true);
    setErr("");
    try {
      const res = await fetch(apiUrl(`contact/${contactId}/${endpointFor(tab)}/${existing!.id}`), {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(await res.text());
      onSaved();
    } catch {
      setErr("Löschen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="kvp-rd-scrim" onClick={onClose} style={{ zIndex: 1500 }}>
      <div className="dm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-titles">
              <div className="dm-kicker">{isEdit ? "Bearbeiten" : "Neu"}</div>
              <h2>{tab}</h2>
            </div>
            <button className="dm-x" onClick={onClose} aria-label="Schließen">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M4.5 4.5l9 9M13.5 4.5l-9 9" /></svg>
            </button>
          </div>
        </div>
        <div className="dm-body">
          <div className="fgrid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {tab === "Anschriften" && (
              <>
                <Field label="Bezeichnung" value={form.addressDescription} onChange={(v) => up("addressDescription", v)} placeholder="z. B. Postanschrift, Zweitwohnsitz" />
                {/* Das Land steht vor der Adresse: es entscheidet, wie PLZ und
                    Straßensuche zu lesen sind. Als Auswahl statt Freitext, sonst
                    driften „Belgien", „belgien" und „BE" auseinander und keine
                    Länderregel greift mehr. */}
                <LandFeld value={String(form.country ?? "")} onChange={(v) => up("country", v)} />
                <AddressAutocomplete
                  label="Straße"
                  name="dfd-street"
                  required
                  value={String(form.street ?? "")}
                  onChange={(v) => up("street", v)}
                  land={String(form.country ?? "")}
                  onResolved={(a) =>
                    setForm((f) => ({
                      ...f,
                      street: a.street,
                      postalCode: a.postalcode,
                      city: a.city,
                      ortsteil: a.cityArea,
                    }))
                  }
                />
                <Field label="Ort *" value={form.city} onChange={(v) => upPlzOderOrt("city", v)} />
                <PlzFeld
                  value={String(form.postalCode ?? "")}
                  land={String(form.country ?? "")}
                  onChange={(v) => upPlzOderOrt("postalCode", v)}
                />
                <Field label="Ortsteil" value={form.ortsteil} onChange={(v) => up("ortsteil", v)} />
                <Check label="Standard-Anschrift" checked={!!form.isDefault} onChange={(v) => up("isDefault", v)} />
              </>
            )}
            {tab === "Kontaktdaten" && (
              <>
                <Sel
                  label="Typ *"
                  value={form.type}
                  onChange={(v) => up("type", v)}
                  options={[
                    { value: "phone", label: "Telefon" },
                    { value: "mobile", label: "Mobil" },
                    { value: "email", label: "E-Mail" },
                    { value: "fax", label: "Fax" },
                    // Bestandszeile eines anderen Typs (z. B. LinkedIn): Option
                    // dynamisch anbieten, sonst verfälscht das Bearbeiten den Typ.
                    ...(form.type && !KERN_TYPEN.includes(String(form.type))
                      ? [{ value: String(form.type), label: String(form.type) }]
                      : []),
                  ]}
                />
                {!istFirma && istKernTyp && (
                  <Sel
                    label="Art *"
                    value={form.kind}
                    onChange={(v) => {
                      // Genau ein Unternehmen → beim Wechsel auf geschäftlich gleich gesetzt.
                      setForm((f) => ({
                        ...f,
                        kind: v,
                        firmenWahl:
                          v === "geschaeftlich" ? firmenMitEinzigem(f.firmenWahl as FirmaWahl[], firmen) : [],
                      }));
                    }}
                    options={[
                      { value: "privat", label: "privat" },
                      { value: "geschaeftlich", label: "geschäftlich" },
                    ]}
                  />
                )}
                <Field
                  label="Bezeichnung"
                  value={form.label}
                  onChange={(v) => up("label", v)}
                  placeholder={istFirma ? "z. B. Zentrale, Buchhaltung" : "z. B. Durchwahl Buchhaltung"}
                />
                {!istFirma && istKernTyp && form.kind === "geschaeftlich" && (
                  (firmen?.length ?? 0) > 0 || (form.firmenWahl as FirmaWahl[]).length > 0 ? (
                    <div style={{ display: "grid", rowGap: 5 }}>
                      <label htmlFor="dm-unternehmen" style={{ fontSize: 12, fontWeight: 600, color: "#6b5a66" }}>
                        Unternehmen
                      </label>
                      <UnternehmenMehrfachWahl
                        id="dm-unternehmen"
                        firmen={firmen ?? []}
                        gewaehlt={form.firmenWahl as FirmaWahl[]}
                        onChange={(neu) => up("firmenWahl", neu)}
                      />
                    </div>
                  ) : (
                    <div style={{ alignSelf: "end", fontSize: 12, color: "#6b5a66", padding: "8px 0" }}>
                      Keine verbundene Firma — Zuordnung bleibt leer.
                    </div>
                  )
                )}
                {["phone", "mobile", "fax"].includes(String(form.type)) ? (
                  <TelefonField
                    label="Nummer *"
                    value={form.value}
                    onChange={(v) => up("value", v)}
                    placeholder="+49 221 12345"
                    // Firma oder Art „geschäftlich": Durchwahl „-" erlaubt.
                    geschaeftlich={istFirma || form.kind === "geschaeftlich"}
                    zentrale={firmenZentrale}
                  />
                ) : (
                  <Field label="Nummer / Adresse *" value={form.value} onChange={(v) => up("value", v)} placeholder="Nummer bzw. Mailadresse" />
                )}
                <Check label="Standard für diesen Typ" checked={!!form.isDefault} onChange={(v) => up("isDefault", v)} />
              </>
            )}
            {tab === "Bankverbindungen" && (
              <>
                <Field label="Bank" value={form.bankName} onChange={(v) => up("bankName", v)} />
                <IbanField label="IBAN" value={form.iban} onChange={(v) => up("iban", v)} onBankErkannt={bankUebernehmen} />
                <Field label="BIC" value={form.bic} onChange={(v) => up("bic", v)} />
                <Field label="Kontoinhaber" value={form.accountHolder} onChange={(v) => up("accountHolder", v)} />
                <Field label="Zweck" value={form.bankDescription} onChange={(v) => up("bankDescription", v)} />
                <Check label="Standard" checked={!!form.isDefault} onChange={(v) => up("isDefault", v)} />
                <Check label="Bankeinzug" checked={!!form.directDebit} onChange={(v) => up("directDebit", v)} />
              </>
            )}
          </div>
          {err && <div style={{ color: "#c23b3b", fontSize: 13, marginTop: 12 }}>{err}</div>}
        </div>
        <div className="dm-foot" style={{ display: "flex", justifyContent: "space-between" }}>
          {isEdit ? (
            <button onClick={del} disabled={saving} style={btn("#c23b3b")}>Löschen</button>
          ) : (
            <span />
          )}
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} disabled={saving} style={btn("#f8f4f8", "#241522")}>Abbrechen</button>
            <button onClick={submit} disabled={saving} style={btn("#7d2169")}>{saving ? "Speichert…" : "Speichern"}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
